import { Nullable, Action } from 'modules/types/common';
import { FormattedEvent } from 'serverTypes/event';
import Logger from 'services/Logger';
import { getTestResultsList } from 'store/testResults/actions';
import { RootDispatch, RootState } from 'store/types';
import {
  hiddenEventListRequestStateSelector,
  eventListDataSelector,
  eventListRequestStateSelector
} from './selectors';
import { PatchEventBody } from './types';

export const GET_EVENT = 'EVENTS.GET_EVENT';
export const GET_EVENT_SUCCESS = 'EVENTS.GET_EVENT_SUCCESS';
export const GET_EVENT_FAIL = 'EVENTS.GET_EVENT_FAIL';

export const getEvent = (eventId: string): Action => ({
  type: GET_EVENT,
  payload: {
    client: 'events',
    request: {
      method: 'get',
      url: `/events/${eventId}`
    }
  }
});

export const GET_EVENT_LIST = 'EVENTS.GET_EVENT_LIST';
export const GET_EVENT_LIST_SUCCESS = 'EVENTS.GET_EVENT_LIST_SUCCESS';
export const GET_EVENT_LIST_FAIL = 'EVENTS.GET_EVENT_LIST_FAIL';

export const getEvents = (category?: string): Action => {
  let query = '?getEventData=true';
  if (category) {
    query += `${query.length ? '&' : '?'}category=${category}`;
  }

  return {
    type: GET_EVENT_LIST,
    payload: {
      client: 'events',
      request: {
        method: 'get',
        url: `/events${query}`
      }
    }
  };
};

export const GET_HIDDEN_EVENT_LIST = 'EVENTS.GET_HIDDEN_EVENT_LIST';
export const GET_HIDDEN_EVENT_LIST_SUCCESS = 'EVENTS.GET_HIDDEN_EVENT_LIST_SUCCESS';
export const GET_HIDDEN_EVENT_LIST_FAIL = 'EVENTS.GET_HIDDEN_EVENT_LIST_FAIL';

export const getHiddenEvents = (useRequestState = true) => ({
  type: GET_HIDDEN_EVENT_LIST,
  payload: {
    client: 'events',
    useRequestState,
    requestStateSelector: hiddenEventListRequestStateSelector,
    request: {
      method: 'get',
      url: `/events?hidden=true`
    }
  }
});

export const GET_UPDATE_EVENT_LIST = 'EVENTS.GET_UPDATE_EVENT_LIST';
export const GET_UPDATE_EVENT_LIST_SUCCESS = 'EVENTS.GET_UPDATE_EVENT_LIST_SUCCESS';
export const GET_UPDATE_EVENT_LIST_FAIL = 'EVENTS.GET_UPDATE_EVENT_LIST_FAIL';

export const getUpdateEvents = (useRequestState = true) => async (dispatch: RootDispatch) => {
  dispatch(getHiddenEvents(false));

  return dispatch({
    type: GET_UPDATE_EVENT_LIST,
    payload: {
      client: 'events',
      useRequestState,
      requestStateSelector: eventListRequestStateSelector,
      request: {
        method: 'get',
        url: `/events?getMedDetails=true&category=update`
      }
    }
  });
};

export const PATCH_EVENT = 'EVENTS.PATCH_EVENT';
export const PATCH_EVENT_SUCCESS = 'EVENTS.PATCH_EVENT_SUCCESS';
export const PATCH_EVENT_FAIL = 'EVENTS.PATCH_EVENT_FAIL';
export const PATCH_EVENT_RESET = 'EVENTS.PATCH_EVENT_RESET';
export const PATCH_EVENT_SKIP = 'EVENTS.PATCH_EVENT_SKIP';
export const SET_PATCH_EVENT_MESSAGE = 'EVENTS.SET_PATCH_EVENT_MESSAGE';

export const patchEventMessage = (message: Nullable<string>) => ({
  type: SET_PATCH_EVENT_MESSAGE,
  message
});

export const patchEvent = (
  eventId: string,
  body: PatchEventBody,
  updateTestResults = false,
  forceUpdate = true
  // When patching the event from patchEventBySourceId. There can be multiple events. We want to hold off on updating till all those events are patched
) => async (dispatch: RootDispatch, getState: () => RootState) => {
  try {
    // Get the current events list
    const state = getState();
    const events: FormattedEvent[] = eventListDataSelector(state) || [];
    // Find the relevant event
    const evt = events.find(e => e.id === eventId);
    // Don't bother with the update if the read flag is already true
    if (evt?.read === true) {
      return dispatch({ type: PATCH_EVENT_SKIP });
    }

    // Update the event
    await dispatch({
      type: PATCH_EVENT,
      payload: {
        client: 'events',
        request: {
          method: 'patch',
          url: `/events/${eventId}`,
          data: body
        }
      }
    });

    dispatch({ type: PATCH_EVENT_RESET });
  } catch (err) {
    Logger.warn('Shared events.actions.patchEvent error: ', err.message);
  }
  if (updateTestResults) {
    dispatch(getTestResultsList(undefined, undefined, false));
  }
  // Pass or fail, get fresh data from the server
  if (forceUpdate) {
    return dispatch(getUpdateEvents(false));
  }

  return dispatch(patchEventMessage('Update Complete'));
};

export const patchEventBySourceId = (sourceId: string, body: PatchEventBody) => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  try {
    // Get the current events list
    const state = getState();
    // Find the relevant event, there might be more than one with that source
    const events: FormattedEvent[] = eventListDataSelector(state) || [];
    const evts: FormattedEvent[] = events.filter(e => e.sourceId === sourceId);

    // See if there is an event not read
    const eventNotRead = evts.find(e => !e.read);

    // Don't patch if you don't need to
    if (evts.length && !eventNotRead) {
      // return no need to get fresh data.
      return dispatch({ type: PATCH_EVENT_SKIP });
    }

    // Patch the events by eventId if you got them
    if (evts.length) {
      await Promise.all(
        evts.map(evt => {
          const updateTestResults =
            evt.sourceResource === 'imagingReport' ||
            evt.sourceResource === 'labResult' ||
            evt.sourceResource === 'pathologyReport';
          return dispatch(patchEvent(evt.id, body, updateTestResults, false));
        })
      );
    } else {
      // Patch by sourceId if you don't have an event.
      await dispatch({
        type: PATCH_EVENT,
        payload: {
          client: 'events',
          request: {
            method: 'patch',
            url: `/events-by-source/${sourceId}`,
            data: body
          }
        }
      });

      dispatch({ type: PATCH_EVENT_RESET });
    }
    // Pass or fail, get fresh data.
    return dispatch(getUpdateEvents(false));
  } catch (err) {
    return dispatch(getUpdateEvents(false));
  }
};

export interface AcknowledgementBody {
  ihcEmpi: string;
  eventId: string;
  sequence: string;
}

export const POST_ACKNOWLEDGE_EVENT = 'EVENTS.POST_ACKNOWLEDGE_EVENT';
export const POST_ACKNOWLEDGE_EVENT_SUCCESS = 'EVENTS.POST_ACKNOWLEDGE_EVENT_SUCCESS';
export const POST_ACKNOWLEDGE_EVENT_FAIL = 'EVENTS.POST_ACKNOWLEDGE_EVENT_FAIL';

export const acknowledgeEvent = (body: AcknowledgementBody): Action => ({
  type: POST_ACKNOWLEDGE_EVENT,
  payload: {
    client: 'eventsGeneration',
    request: {
      method: 'post',
      url: '/antiCoagulation',
      data: body
    }
  }
});

export const DISMISS_EVENT = 'EVENTS.DISMISS_EVENT';
export const DISMISS_EVENT_FAIL = 'EVENTS.DISMISS_EVENT_FAIL';
export const DISMISS_EVENT_SUCCESS = 'EVENTS.DISMISS_EVENT_SUCCESS';

export const dismissEvent = (eventId: string): Action => ({
  type: DISMISS_EVENT,
  payload: {
    client: 'events',
    request: {
      method: 'delete',
      url: `/events/${eventId}`
    }
  }
});

export const IS_EVENT_EXPIRED = 'EVENTS.IS_EVENT_EXPIRED';
export const IS_EVENT_EXPIRED_FAIL = 'EVENTS.IS_EVENT_EXPIRED_FAIL';
export const IS_EVENT_EXPIRED_SUCCESS = 'EVENTS.IS_EVENT_EXPIRED_SUCCESS';

export const isEventExpired = (eventId: string): Action => ({
  type: IS_EVENT_EXPIRED,
  payload: {
    client: 'events',
    request: {
      method: 'get',
      url: `/events/${eventId}/link-expired`
    }
  }
});
